import Alert from '@risk-smart/themed-cloudscape-components/alert';
import Textarea from '@risk-smart/themed-cloudscape-components/textarea';
import Button from '@risksmart-app/components/src/button';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import Modal from '@risksmart-app/components/src/modal';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Loading from 'src/components/loading';
import { z } from 'zod';

import { CHANGE_REQUEST_OVERRIDE_ACTION } from '@/components/change-request-override-modal/changeRequestTypes';
import { useOverrideChangeRequest } from '@/hooks/mutations/change-request';

import styles from '../change-request-levels/style.module.scss';
import { FormField } from '../form/form/FormField';

interface Props {
  changeRequestId: string;
  onDismiss: () => void;
  visible: boolean;
  mode: CHANGE_REQUEST_OVERRIDE_ACTION;
  activeLevel: string;
}

const ChangeRequestOverrideModal: FC<Props> = ({
  changeRequestId,
  onDismiss,
  visible,
  mode,
  activeLevel,
}) => {
  const { t } = useTranslation('common');
  const { t: ta } = useTranslation('common', { keyPrefix: 'approvals' });

  const [rationale, setRationale] = useState<string | undefined>();
  const [errors, setErrors] = useState<{ rationale: string | undefined }>({
    rationale,
  });
  const { user, isLoading } = useRisksmartUser();
  const { addNotification } = useNotifications();

  // RSP-1528 §11 — flag-gated override (tRPC when the `trpc` flag is on, else
  // the legacy Apollo mutations). Covers BOTH the whole-CR override and the
  // single-level override (SKIP). Errors are surfaced by the onSubmit
  // try/catch below (the hook itself doesn't toast).
  const {
    overrideChangeRequest,
    overrideChangeRequestLevel,
    loading: updating,
  } = useOverrideChangeRequest();

  const schema = z.object({
    rationale: z
      .string({ required_error: 'You must provide a rationale' })
      // `.trim()` BEFORE `.min(1)`, matching the server's
      // `z.string().trim().min(1)` (COI-622 A25). Without it a whitespace-only
      // rationale passed here and came back as a raw 400 from the router
      // instead of an inline field error — and because `.trim()` transforms,
      // `validate()` now returns the trimmed value, so that is what gets sent.
      .trim()
      .min(1, 'You must provide a rationale'),
    approved: z.boolean(),
  });

  /**
   * Returns the VALIDATED rationale, or `null` when it fails.
   *
   * Returning the value rather than a boolean is what lets the callers below
   * pass `comment` without a `?? '-'` fallback (COI-622 A24). `rationale` is
   * `string | undefined` state, so a boolean-returning validate left the
   * callers unable to narrow it, and the fallback existed purely to satisfy
   * the type — which meant a literal `'-'` would satisfy the server's new
   * mandatory-rationale bound if this validation were ever relaxed.
   *
   * `approved: true` is hard-coded into the parse and `z.boolean()` cannot
   * reject it, so a rationale issue is the only reachable failure; the old
   * "fail, but only block on a rationale issue" branch could not fire.
   */
  const validate = (): string | null => {
    const result = schema.safeParse({ rationale, approved: true });

    if (!result.success) {
      setErrors({
        rationale: result.error.issues.find((i) => i.path[0] === 'rationale')
          ?.message,
      });

      return null;
    }

    setErrors({ rationale: undefined });

    return result.data.rationale;
  };

  const onSubmit = async (approved: boolean) => {
    const validatedRationale = validate();
    if (validatedRationale === null) {
      return;
    }

    try {
      if (mode === CHANGE_REQUEST_OVERRIDE_ACTION.OVERRIDE) {
        await overrideChangeRequest({
          changeRequestId,
          comment: validatedRationale,
          approved,
        });
      } else {
        // SKIP — override a single approval level (flag-gated: tRPC
        // `changeRequest.overrideLevel` or the legacy Apollo approver-response
        // action, both Manager-gated).
        await overrideChangeRequestLevel({
          changeRequestId,
          levelId: activeLevel,
          approved,
          comment: `Overridden by ${
            user?.claims_username ?? 'a risk manager'
          }. Rationale: ${validatedRationale}`,
        });
        setRationale('');
      }

      onDismiss();
    } catch (error) {
      addNotification({
        type: 'error',
        content: error instanceof Error ? error.message : String(error),
      });
    }
  };
  if (isLoading) {
    return <Loading />;
  }

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header={
        mode === CHANGE_REQUEST_OVERRIDE_ACTION.SKIP
          ? ta('overrideModalStepHeader')
          : ta('overrideModalOverallHeader')
      }
    >
      <div className={'flex flex-col gap-6'}>
        <Alert type={'warning'}>
          {mode === CHANGE_REQUEST_OVERRIDE_ACTION.SKIP
            ? ta('override_step_alert')
            : ta('override_change_request_modal_alert')}
        </Alert>
        <FormField label={'Rationale'} errorText={errors.rationale}>
          <Textarea
            name={'rationale'}
            value={rationale ?? ''}
            onChange={(e) => {
              setRationale(e.detail.value);
            }}
          />
        </FormField>
        <div className={'flex flex-row gap-3'}>
          <Button
            variant={'primary'}
            onClick={async () => await onSubmit(true)}
            disabled={updating}
          >
            {t('approve')}
          </Button>
          <Button
            onClick={async () => await onSubmit(false)}
            {...{ className: styles.dangerButton }}
            disabled={updating}
          >
            {t('reject')}
          </Button>
          <Button onClick={onDismiss} disabled={updating}>
            {t('cancel')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ChangeRequestOverrideModal;
